import React, { useState, useEffect, useRef } from "react"
import { Button, Box, Chip, LinearProgress, CircularProgress, Collapse, Paper, Portal, Typography, Divider } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import StopIcon from '@mui/icons-material/Stop';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Draggable from 'react-draggable';
import "./recordActivity.css";
import axios from "axios";
import { APIURL } from "../../../config";
import TextField from "@mui/material/TextField";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from "@mui/material/IconButton";
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/styles'
import MUIRichTextEditor from 'mui-rte';
import { ContentState, EditorState, convertToRaw, convertFromRaw, Modifier, SelectionState } from 'draft-js';
import { borderBottom, borderLeft, borderRight, fontWeight } from "@mui/system";
import moment from "moment";
import MicIcon from '@mui/icons-material/Mic';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { FormControl, FormControlLabel, FormLabel, InputLabel, MenuItem, Radio, RadioGroup, Select } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { convertTime } from "src/utils/time";

const myTheme = createTheme({
    overrides: {
        MUIRichTextEditor: {
            container: {
                paddingLeft: 5
            },
            editor: {
                backgroundColor: "white",
                padding: "20px",
                minHeight: "200px",
                borderBottom: "1px solid lightblue",
                borderLeft: "1px solid lightblue",
                borderRight: "1px solid lightblue",
            },
            toolbar: {
                border: "1px solid gray",
                backgroundColor: "#ebebeb",
            },
            placeHolder: {
                color: "gray",
                borderTop: "1px solid lightblue",
                borderLeft: "1px solid lightblue",
                borderRight: "1px solid lightblue",
                padding: 20,
            },
            anchorLink: {
                color: "lightblue",
                textDecoration: "underline",
            },
        },
    },
});

const CHIP_COLOR = '#1976d2';

// Above MUI's modal layer (1300) so the guidance window floats over the note dialogs.
const GUIDANCE_WINDOW_ZINDEX = 1400;
// Height reserved for the fixed "Tours" launcher pinned to the bottom-left of every page
const TOURS_LAUNCHER_CLEARANCE = 88;
const GUIDANCE_POSITION_KEY = 'noteGuidanceWindowPosition';

const scoreColor = (score) => (score >= 80 ? '#4caf50' : score >= 50 ? '#ff9800' : '#f44336');

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const NOTE_PRIVACY_OPTIONS = [
    { value: 'shared', label: 'Shared with All' },
    { value: 'private', label: 'Private to Program' },
];

const selectableChipSx = (selected, color = CHIP_COLOR) => ({
    flex: 1,
    justifyContent: 'center',
    bgcolor: selected ? color : 'transparent',
    color: selected ? '#fff' : color,
    border: `1px solid ${color}`,
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': { bgcolor: selected ? color : `${color}1a` },
});

const SectionCard = ({ icon, title, children, flex = '1 1 0' }) => (
    <Paper
        elevation={1}
        sx={{
            flex,
            minWidth: 280,
            p: 2,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#ffffff',
            border: '1px solid #e0e7ef',
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            {icon}
            <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600, color: '#2c3e50' }}>
                {title}
            </Typography>
        </Box>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ flex: 1 }}>{children}</Box>
    </Paper>
);

/*
~~~ props ~~~
note_id
mode
user
onSubmit
com_id
twoColumn   // renders the form as two side-by-side cards, matching the
            // New Task / Project / Contact / Customer forms. Defaults to on;
            // pass twoColumn={false} for the old stacked layout.
*/
export default function NoteFormv2(props) {
    const initialPlainText = useRef('');  // store initial plain text for dirty comparison
    const [customerList, setCustomerList] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [projectList, setProjectList] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [taskList, setTaskList] = useState([]);
    const [selectedTask, setSelectedTask] = useState(null);
    const [noteText, setNoteText] = useState(JSON.stringify(convertToRaw(EditorState.createEmpty().getCurrentContent())));
    const [noteTitle, setNoteTitle] = useState('');
    const [defaultRteValue, setDefaultRteValue] = useState(`{"blocks":[{"key":"block1","text":"","type":"unstyled","depth":0,"inlineStyleRanges":[],"entityRanges":[],"data":{}}],"entityMap":{}}`)
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const [taskTypes, setTaskTypes] = useState([]);
    const [selectedTaskType, setSelectedTaskType] = useState(null);
    const [fieldError, setFieldError] = useState(false);
    const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
    const [taskTitle, setTaskTitle] = useState("");
    const [startTime, setStartTime] = useState(moment().format('YYYY-MM-DDTHH:mm:ss'));
    const [endTime, setEndTime] = useState(moment().format('YYYY-MM-DDTHH:mm:ss'));
    const [duration, setDuration] = useState("");
    const [noteTextEditorState, setNoteTextEditorState] = useState(EditorState.createEmpty());
    const [selectedProjectLocation, setSelectedProjectLocation] = useState(null);
    const [notePrivacy, setNotePrivacy] = useState('shared');
    const canSetPrivateNote = !!(selectedProject && selectedProject.pro_program_id);
    const durations = Array.from({ length: 12 * 4 }, (_, i) => {
        const hours = Math.floor(i / 4);
        const minutes = (i % 4) * 15;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    });

    const [useDurationMode] = useState(() => {
        // Check for feature_id 76 first (explicit time mode setting)
        const timeModeSettings = props.user?.comp_settings.find(setting => setting.feature_id === 76);
        console.log('timeModeSettings:', timeModeSettings);
        if (timeModeSettings) {
            console.log('timeModeSettings.feature_value:', timeModeSettings.feature_value);
            return timeModeSettings.feature_value === 'duration';
        }
        // Fallback to feature_id 101/104 check (business type)
        return props.user.comp_settings.some(setting => (setting.feature_id === 101 || setting.feature_id === 104) && setting.feature_value === "primaryBusinessType");
    });

    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition();

    const [errorAlert, setErrorAlert] = useState('')
    const handleErrorAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        props?.handleClose?.();
        setErrorAlert('')
        if (props.isVoiceNote) {
            props.onSubmit();
        }
    }

    const [successAlert, setSuccessAlert] = useState('')
    const handleSuccessAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        props?.handleClose?.();
        setSuccessAlert('')
        if (props.isVoiceNote) {
            props.onSubmit();
        }
    }

    const MAX_NOTE_LENGTH = 6000;
    const [charCount, setCharCount] = useState(0);
    const [pasteLimitAlert, setPasteLimitAlert] = useState(false);

    const [isRecording, setIsRecording] = useState(false);
    const [audioURL, setAudioURL] = useState('');
    const [recordedAudio, setRecordedAudio] = useState(null);
    const [microphoneStream, setMicrophoneStream] = useState(null);
    const [timer, setTimer] = useState(0);
    const voicedTranscript = useRef('');
    const engaged = useRef(false);
    const editorStateRef = useRef(EditorState.createEmpty());
    const prevTranscriptRef = useRef('');

    // AI Guidance state
    const [guidanceResult, setGuidanceResult] = useState(null);
    const [guidanceLoading, setGuidanceLoading] = useState(false);
    const [guidanceError, setGuidanceError] = useState('');
    // The guidance results live in a non-modal floating window so the note
    // stays editable while they are on screen. It is draggable by its header,
    // and the last position is remembered across notes.
    const [guidanceOpen, setGuidanceOpen] = useState(false);
    const [guidancePosition, setGuidancePosition] = useState({ x: 0, y: 0 });
    const guidanceRef = useRef(null);

    // A position saved on a bigger screen can land off-screen here, so pull the
    // window back into view whenever it opens or the viewport changes.
    const keepGuidanceOnScreen = (pos) => {
        const el = guidanceRef.current;
        if (!el) return pos;
        const rect = el.getBoundingClientRect();
        const maxTop = Math.max(0, window.innerHeight - TOURS_LAUNCHER_CLEARANCE - rect.height);
        return {
            x: pos.x + (clamp(rect.left, 0, Math.max(0, window.innerWidth - rect.width)) - rect.left),
            y: pos.y + (clamp(rect.top, 0, maxTop) - rect.top),
        };
    };

    useEffect(() => {
        try {
            const saved = localStorage.getItem(GUIDANCE_POSITION_KEY);
            if (saved) setGuidancePosition(JSON.parse(saved));
        } catch (err) {
            console.log(err);
        }
    }, []);

    useEffect(() => {
        if (!guidanceOpen) return;
        const settle = () => setGuidancePosition((pos) => {
            const next = keepGuidanceOnScreen(pos);
            return next.x === pos.x && next.y === pos.y ? pos : next;
        });
        settle();
        window.addEventListener('resize', settle);
        return () => window.removeEventListener('resize', settle);
    }, [guidanceOpen]);

    useEffect(() => {
        if (isRecording) {
            let interval = setInterval(() => setTimer(prevTimer => prevTimer + 1), 1000);
            return () => clearInterval(interval);
        }
    }, [isRecording]);

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            try {
                const response = await axios.get(`${APIURL}/projlocations`, {
                    params: {
                        project_id: selectedProject ? selectedProject.pro_id : null
                    }
                });
                // Ignore a response that lands after the project changed again,
                // otherwise it can wipe the location of the project now selected.
                if (cancelled) return;
                setSelectedProjectLocation(response.data.length > 0 ? response.data[0] : null);
            } catch (err) {
                console.log(err);
                if (!cancelled) setSelectedProjectLocation(null);
            }
        }
        fetchData();
        return () => { cancelled = true; };
    }, [selectedProject]);

    useEffect(() => {
        if (!canSetPrivateNote && notePrivacy === 'private') {
            setNotePrivacy('shared');
        }
    }, [canSetPrivateNote]);

    const onStartRecording = async () => {
        try {
            // Clear previous state and resources
            setIsRecording(false);
            setAudioURL('');
            setRecordedAudio(null);
            setTimer(0);

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            setMicrophoneStream(stream);
            const recorder = new MediaRecorder(stream);
            recorder.ondataavailable = (event) => setRecordedAudio(event.data);
            recorder.start();
            setIsRecording(true);
        } catch (error) {
            console.error(error);
        }
    };

    const onStopRecording = () => {
        microphoneStream.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
    };

    useEffect(() => {
        if (!listening) {
            voicedTranscript.current = transcript;
            engaged.current = false;
            resetTranscript();
            prevTranscriptRef.current = '';
        } else {
            engaged.current = true;
        }
    }, [listening]);

    const onPlayRecording = () => {
        const url = URL.createObjectURL(recordedAudio);
        setAudioURL(url)
    };

    useEffect(() => {

        const fetchTaskTypes = async () => {
            await axios.get(`${APIURL}/dropdowns/dropdownType/taskType`, {
                params: {
                    comp_id: props.user.comp_id,
                    employeeType: props.user.emp_type
                }
            })
                .then(response => {
                    let newTaskTypes = response.data;
                    setTaskTypes(newTaskTypes);
                })
        }

        fetchTaskTypes();
    }, [])

    useEffect(() => {
        let mounted = true;  
        
        // set initial plain text (empty or loaded) on mount
        initialPlainText.current = editorState.getCurrentContent().getPlainText();

        const fetchData = async () => {
            if (props.note_id && props.mode == 'edit') {
                const noteDataResponse = await axios.get(APIURL + `/comments/details`, {
                    params: { comment_id: props.note_id, empID: props.user?.emp_id }
                });
                const noteData = noteDataResponse.data[0];

                setSelectedCustomer({ cus_id: noteData.cus_id ? noteData.cus_id : "", cus_name: noteData.cus_name ? noteData.cus_name : "" });
                setSelectedProject({ pro_id: noteData.pro_id ? noteData.pro_id : "", pro_title: noteData.pro_title ? noteData.pro_title : "", pro_program_id: noteData.pro_program_id ?? null });
                setSelectedTask({ tas_id: noteData.tas_id ? noteData.tas_id : "", tas_title: noteData.tas_title ? noteData.tas_title : "" });
                setDefaultRteValue(noteData.comment_text);
                setNoteText(noteData.comment_text);
                setNoteTitle(noteData.note_title);
                setNotePrivacy(noteData.note_privacy || 'shared');
                // initialize initial plain text from loaded JSON
                try {
                    const raw = JSON.parse(noteData.comment_text);
                    const plain = convertFromRaw(raw).getPlainText();
                    initialPlainText.current = plain;
                } catch (e) {
                    initialPlainText.current = '';
                }
             } else if (props.customer_id) {
                const customerResponse = await axios.get(APIURL + `/customers?com_id=${props.company_id}&cust_id=${props.customer_id}`)
                setSelectedCustomer(customerResponse.data[0])
                setCustomerList([customerResponse.data[0]])
            } else if (props.project_id) {
                const response = await axios.get(APIURL + `/project?proj_id=${props.project_id}&comp_id=${props.company_id}`)
                const customerResponse = await axios.get(APIURL + `/customers?com_id=${props.company_id}&cust_id=${response.data.pro_customer_id}`)
                setSelectedCustomer(customerResponse.data[0])
                setTimeout(() => setSelectedProject(response.data), 100);
            } else {
                try {
                    const response = await axios.get(APIURL + `/customers/assigned?emp_id=${props.user.emp_id}`)
                    setCustomerList(response.data.customers)
                }
                catch (err) {
                    setErrorAlert('An error occured while loading customers')
                }
            }
            // Fetch Data Depending on Case
            // if (props.task_id) {
            //     //TODO IF NEEDED
            //     const response = await axios.get(APIURL + `/project?proj_id=${props.project_id}&comp_id=${props.company_id}`)
            //     const customerResponse = await axios.get(APIURL + `/customers?com_id=${props.company_id}&cust_id=${response.data.pro_customer_id}`)
            //     const taskResponse = await axios.get(APIURL + `/task?taskId=${props.task_id}`)
            //     setSelectedTask(taskResponse.data[0]);
            //     setSelectedCustomer(customerResponse.data[0]);
            //     setSelectedProject(response.data);
            // }
            // else if (props.project_id) {
            //     const response = await axios.get(APIURL + `/project?proj_id=${props.project_id}&comp_id=${props.company_id}`)

            //     console.log(response.data)
            //     const customerResponse = await axios.get(APIURL + `/customers?com_id=${props.company_id}&cust_id=${response.data.pro_customer_id}`)
            //     setSelectedCustomer(customerResponse.data[0])
            //     await setTimeout(() => setSelectedProject(response.data), 500);
            // }
            // else if (props.customer_id) {
            //     const customerResponse = await axios.get(APIURL + `/customers?com_id=${props.company_id}&cust_id=${props.customer_id}`)
            //     console.log(customerResponse.data[0])
            //     setSelectedCustomer(customerResponse.data[0])
            // } else {
            //     try {
            //         const response = await axios.get(APIURL + `/customers/assigned?emp_id=${props.user.emp_id}`)
            //         setCustomerList(response.data.customers)
            //     }
            //     catch (err) {
            //         setErrorAlert('An error occured while loading customers')
            //     }
            // }

            // // Get Note Text for Edit Mode
            // if (props.note_id && props.mode == 'edit') {
            //     const response = await axios.get(APIURL + `/comments?comment_id=${props.note_id}`)
            //     setDefaultRteValue(isJSON(response.data[0].comment_text) ? response.data[0].comment_text : `{"blocks":[{"key":"block1","text":"${response.data[0].comment_text.replace(/\n/g, ' ')}","type":"unstyled","depth":0,"inlineStyleRanges":[],"entityRanges":[],"data":{}}],"entityMap":{}}`)
            //     setNoteText(response.data[0].comment_text)
            //     setNoteTitle(response.data[0].note_title)
            // }
        }
        fetchData();
        return () => { mounted = false; engaged.current = false; SpeechRecognition.stopListening(); };
    }, []);

    useEffect(() => {
        const loadCustomerProjects = async () => {
            try {
                if (props.mode === 'edit') {
                    return;
                }
                setSelectedProject(null);
                setProjectList([]);
                if (selectedCustomer != null) {
                    await getCustomerProjects();
                }
            } catch (error) {
                console.error('Error loading customer projects:', error);
            }
        };
        loadCustomerProjects();
    }, [selectedCustomer]);

    useEffect(() => {
        const loadProjectTasks = async () => {
            try {
                if (props.mode === 'edit') {
                    return;
                }
                setSelectedTask(null);
                setTaskList([]);
                if (selectedProject != null) {
                    await getProjectTasks();
                }
            } catch (error) {
                console.error('Error loading project tasks:', error);
            }
        };
        loadProjectTasks();
    }, [selectedProject]);

    const getCustomerProjects = async () => {
        try {
            const response = await axios.get(APIURL + `/project?cus_id=${selectedCustomer.cus_id}`)
            setProjectList(response.data)
        }
        catch (err) {
            console.log(err)
            setErrorAlert(`An error while loading projects for the selected customer`)
        }
    }

    const getProjectTasks = async () => {
        try {
            const response = await axios.get(APIURL + `/task?pro_id=${selectedProject.pro_id}`)
            if (props.task_id) {
                let tasks = response.data;
                setSelectedTask(tasks.filter(t => t.tas_id === props.task_id)[0])
            }
            
            // Filter out inactive tasks and tasks with checkoutEmpDate older than 1 year
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
            
            let activeTasks = response.data.filter(t => {
                // First check if task is active
                if (t.tas_is_active === 2) return false;
                
                // Check checkoutEmpDate - include if null, empty, or less than 1 year old
                if (!t.checkoutEmpDate || t.checkoutEmpDate === '') return true;
                
                try {
                    const checkoutDate = new Date(t.checkoutEmpDate);
                    return checkoutDate > oneYearAgo;
                } catch (error) {
                    // If date parsing fails, include the task by default
                    return true;
                }
            });
            
            let tempTL = [{ tas_title: 'Add New Task (Timesheet)', tas_id: -1 }, ...activeTasks];
            setTaskList(tempTL)
        }
        catch (err) {
            console.log(err)
            setErrorAlert(`An error while loading tasks for the selected project`)
        }
    }

    const toNumberOrNull = (value) => {
        const num = parseFloat(value);
        return Number.isFinite(num) ? num : null;
    }

    const createVoiceNote = async () => {
        try {
            props.checkTokenExpiry()
        } catch (err) {
            console.log(err)
        }

        let errorWhileSaving = false;
        let assignedTaskId = null;

        if (selectedCustomer && selectedProject && selectedTask && recordedAudio) {
            if (selectedTask.tas_id === -1) {
                if (taskDetsValidator()) {
                    await axios.post(APIURL + '/task', {
                        empId: props.user.emp_id,
                        empName: props.user.emp_first_name + ' ' + props.user.emp_last_name,
                        company_id: props.user.comp_id,
                        tTitle: taskTitle,
                        tDesc: 'Time Sheet Entry',
                        tProject: selectedProject.pro_id,
                        tPreReq: 'NULL',
                        tType: selectedTaskType,
                        tStrtDate: moment(startTime).format('YYYY-MM-DD'),
                        tEndDate: moment(endTime).format('YYYY-MM-DD'),
                        tStartTime: convertTime(startTime, moment.tz.guess(), props.user.emp_timezone, 'YYYY-MM-DDTHH:mm:ss') ?? moment(startTime).format('YYYY-MM-DDTHH:mm:ss'),
                        tEndTime: convertTime(endTime, moment.tz.guess(), props.user.emp_timezone, 'YYYY-MM-DDTHH:mm:ss') ?? moment(endTime).format('YYYY-MM-DDTHH:mm:ss'),
                        tLoc: selectedProjectLocation?.loc_id ?? null,
                        tActive: 1,
                        tAssTo: props.user.emp_id,
                        tPriority: 'Low',
                        tIsBillable: true,
                        tNotes: 'N/A',
                        user_id: props.user.emp_id,
                        task_emp_timezone: props.user.emp_timezone || ''
                    }).then(async (response) => {
                        console.log(response);
                        if (response?.data?.code !== 200 || !response?.data?.id) {
                            errorWhileSaving = true;
                            setErrorAlert('An error occured while trying to create the task');
                            return;
                        }
                        assignedTaskId = response.data.id;
                        await axios.post(APIURL + '/timesheets', {
                            assignedById: props.user.emp_id,
                            projId: selectedProject.pro_id,
                            empId: props.user.emp_id,
                            empName: props.user.emp_first_name + ' ' + props.user.emp_last_name,
                            companyId: props.user.comp_id,
                            taskName: taskTitle,
                            locId: selectedProjectLocation?.loc_id ?? null,
                            taskId: response.data.id,
                            emploc_lat: toNumberOrNull(selectedProjectLocation?.location_lat),
                            emploc_lon: toNumberOrNull(selectedProjectLocation?.location_lon),
                            start_time: startTime,
                            end_time: endTime,
                            timesheet_status: 1,
                            empLocate_timezone: moment.tz.guess()
                        }).then((resp) => {
                            console.log(resp);
                        }).catch((err) => {
                            console.log(err);
                            errorWhileSaving = true;
                            setErrorAlert('An error occured while trying to create the timesheet')
                        })

                    }).catch((err) => {
                        console.log(err);
                        errorWhileSaving = true;
                        setErrorAlert('An error occured while trying to create the task')
                    })

                } else {
                    errorWhileSaving = true;
                }
            }

            const formData = new FormData();

            formData.append("audio_note", recordedAudio, "voice_note.webm");
            formData.append("emp_id", props.user.emp_id);
            formData.append("project_id", selectedProject.pro_id);
            formData.append("task_id", selectedTask.tas_id === -1 ? assignedTaskId : selectedTask.tas_id);
            formData.append("customer_id", selectedCustomer.cus_id);
            formData.append("note_title", noteTitle);

            if (!errorWhileSaving) {
                let response = await axios.post(APIURL + '/audio_note', formData, {
                    headers: {
                        "content-type": "multipart/form-data",
                    },
                }).then(function (response) {
                    setSuccessAlert('Voice Note successfully created')
                }).catch(function (error) {
                    console.log(error);
                    setErrorAlert('An error occured while trying to create the voice note')
                })
            }
        } else {
            if (!selectedCustomer) {
                setErrorAlert(`A selected customer is required to create a voice note`)
            } else if (!selectedProject) {
                setErrorAlert(`A selected project is required to create a voice note`)
            } else if (!selectedTask) {
                setErrorAlert(`A selected task is required to create a voice note`)
            } else {
                setErrorAlert(`Voice Note cannot be empty, please try again.`)
            }
        }
    }

    const taskDetsValidator = () => {
        if (!taskTitle) {
            setErrorAlert(`A task title is required to create a note associated to a new task`);
            return false;
        } else if (!selectedTaskType) {
            setErrorAlert(`A valid task type is required to create a note associated to a new task`);
            return false;
        } else if (useDurationMode && !duration) {
            setErrorAlert(`A valid duration is required to create a note associated to a new task`);
            return false;
        } else if (!useDurationMode && (!startTime || !endTime || !moment(endTime).isAfter(moment(startTime)))) {
            setErrorAlert(`Valid start and end times are required, and end time must be after start time`);
            return false;
        }
        return true;
    }

    useEffect(() => {
        if (!listening) {
            return;
        }
        const delta = transcript.slice(prevTranscriptRef.current.length);
        if (!delta) return;
        prevTranscriptRef.current = transcript;

        const currState = editorStateRef.current;
        const blockMap = currState.getCurrentContent().getBlockMap();
        const key = blockMap.last().getKey();
        const length = blockMap.last().getLength();
        const selection = new SelectionState({
            anchorKey: key,
            anchorOffset: length,
            focusKey: key,
            focusOffset: length,
        });

        const processedDelta = replacePunctuationWords(delta);
        const nextContentState = Modifier.insertText(currState.getCurrentContent(), selection, processedDelta);
        let nextEditorState = EditorState.push(currState, nextContentState, 'voice-to-text');
        const newEditorState = EditorState.moveSelectionToEnd(nextEditorState, nextContentState.getSelectionAfter());
        editorStateRef.current = newEditorState;
        setEditorState(newEditorState);
        setDefaultRteValue(JSON.stringify(convertToRaw(newEditorState.getCurrentContent())));
    }, [transcript])

    const createNote = async () => {
        try {
            props.checkTokenExpiry()
        } catch (err) {
            console.log(err)
        }
        let errorWhileSaving = false;

        if (selectedCustomer && editorState.getCurrentContent().hasText()) {
            let assignedTaskId = null;
            if (selectedTask && selectedTask.tas_id === -1) {
                if (taskDetsValidator()) {
                    await axios.post(APIURL + '/task', {
                        empId: props.user.emp_id,
                        empName: props.user.emp_first_name + ' ' + props.user.emp_last_name,
                        company_id: props.user.comp_id,
                        tTitle: taskTitle,
                        tDesc: 'Time Sheet Entry',
                        tProject: selectedProject.pro_id,
                        tPreReq: 'NULL',
                        tType: selectedTaskType,
                        tStrtDate: moment(startTime).format('YYYY-MM-DD'),
                        tEndDate: moment(endTime).format('YYYY-MM-DD'),
                        tStartTime: convertTime(startTime, moment.tz.guess(), props.user.emp_timezone, 'YYYY-MM-DDTHH:mm:ss') ?? moment(startTime).format('YYYY-MM-DDTHH:mm:ss'),
                        tEndTime: convertTime(endTime, moment.tz.guess(), props.user.emp_timezone, 'YYYY-MM-DDTHH:mm:ss') ?? moment(endTime).format('YYYY-MM-DDTHH:mm:ss'),
                        tLoc: selectedProjectLocation?.loc_id ?? null,
                        tActive: 1,
                        tAssTo: props.user.emp_id,
                        tPriority: 'Low',
                        tIsBillable: true,
                        tNotes: 'N/A',
                        user_id: props.user.emp_id,
                        task_emp_timezone: props.user.emp_timezone || ''
                    }).then(async (response) => {
                        console.log(response);
                        if (response?.data?.code !== 200 || !response?.data?.id) {
                            errorWhileSaving = true;
                            setErrorAlert('An error occured while trying to create the task');
                            return;
                        }
                        assignedTaskId = response.data.id;
                        await axios.post(APIURL + '/timesheets', {
                            assignedById: props.user.emp_id,
                            projId: selectedProject.pro_id,
                            empId: props.user.emp_id,
                            empName: props.user.emp_first_name + ' ' + props.user.emp_last_name,
                            companyId: props.user.comp_id,
                            taskName: taskTitle,
                            locId: selectedProjectLocation?.loc_id ?? null,
                            taskId: response.data.id,
                            emploc_lat: toNumberOrNull(selectedProjectLocation?.location_lat),
                            emploc_lon: toNumberOrNull(selectedProjectLocation?.location_lon),
                            start_time: startTime,
                            end_time: endTime,
                            timesheet_status: 1,
                            empLocate_timezone: moment.tz.guess()
                        }).then((resp) => {
                            console.log(resp);
                        }).catch((err) => {
                            console.log(err);
                            errorWhileSaving = true;
                            setErrorAlert('An error occured while trying to create the timesheet')
                        })

                    }).catch((err) => {
                        console.log(err);
                        errorWhileSaving = true;
                        setErrorAlert('An error occured while trying to create the task')
                    })

                } else {
                    errorWhileSaving = true;
                }
            }
            console.log(errorWhileSaving);
            if (!errorWhileSaving) {
                await axios.post(APIURL + `/comments`, {
                    cus_id: selectedCustomer.cus_id,
                    pro_id: selectedProject ? selectedProject.pro_id : null,
                    tas_id: selectedTask ? (selectedTask.tas_id === -1 ? assignedTaskId : selectedTask.tas_id) : null,
                    emp_id: props.user.emp_id,
                    comment_text: noteText,
                    note_title: noteTitle,
                    note_privacy: notePrivacy,
                    note_quality_rubric_score: guidanceResult?.completeness_score ?? null,
                    companyId: props.user.comp_id,
                    empName: props.user.emp_name,
                }).then(function (response) {
                    console.log(response);
                    setSuccessAlert('Note successfully created')
                }).catch(function (error) {
                    console.log(error);
                    setErrorAlert('An error occured while trying to create the note')
                });
            }
        } else {
            errorWhileSaving = true;
            if (!selectedCustomer) {
                setErrorAlert(`A selected customer is required to create a note`)
            }
            else {
                setErrorAlert(`Note cannot be empty, please try again.`)
            }
        }
        if (!errorWhileSaving) {
            props.onSubmit();
        }
    }

    const updateNote = async () => {
        props.checkTokenExpiry()
        await axios.put(APIURL + `/comments`, {
            cus_id: selectedCustomer.cus_id,
            pro_id: selectedProject ? selectedProject.pro_id : null,
            tas_id: selectedTask ? selectedTask.tas_id : null,
            emp_id: props.user.emp_id,
            companyId: props.user.comp_id,
            empName: props.user.emp_name,
            comment_id: props.note_id,
            comment_text: noteText,
            note_title: noteTitle,
            note_privacy: notePrivacy,
            note_quality_rubric_score: guidanceResult?.completeness_score ?? null,
            updated_by: props.user.emp_id,
        })
            .then(function (response) {
                setSuccessAlert('Note successfully updated')
            })
            .catch(function (error) {
                console.log(error);
                setErrorAlert('An error occured while trying to update the note')
            });

        props.onSubmit()
    }

    const replacePunctuationWords = (text) => {
        const punctuationMap = {
            'comma': ',',
            'period': '.',
            'question mark': '?',
            'exclamation mark': '!',
            'colon': ':',
            'semicolon': ';',
            'dash': '-',
            'hyphen': '-',
            'new line': '\n',
            'new paragraph': '\n\n'
        };

        // Replace punctuation words
        let processedText = text.replace(/\b(comma|period|question mark|exclamation mark|colon|semicolon|dash|hyphen|new line|new paragraph)\b/gi, match => {
            return punctuationMap[match.toLowerCase()] || match;
        });

        // Remove spaces before punctuation
        processedText = processedText.replace(/\s+([,\.?!:;\-\n])/g, '$1');

        return processedText;
    };

    const changeTheEditorState = (state) => {
        return new Promise((resolve) => {
            function checkIfDoneListening() {
                if (!engaged.current) {
                    // All transcript text already inserted via real-time deltas;
                    // just sync noteText and noteTextEditorState from current editor.
                    const currentState = editorStateRef.current;
                    setNoteText(JSON.stringify(convertToRaw(currentState.getCurrentContent())));
                    setNoteTextEditorState(currentState);
                    voicedTranscript.current = '';
                    resolve();
                } else {
                    setTimeout(checkIfDoneListening, 300);
                }
            }
            checkIfDoneListening();
        });
    }

    const onEditorChange = (state) => {
        // More info about EditorState object at
        // https://draftjs.org/docs/api-reference-editor-state
        //

        // Get current selection
        // console.log(state.getSelection());

        // Get current content
        // console.log(JSON.stringify(convertToRaw(state.getCurrentContent())));
        setEditorState(state);
        editorStateRef.current = state;
        setNoteTextEditorState(state);
        const content = convertToRaw(state.getCurrentContent());
        setNoteText(JSON.stringify(content));
        // Get current text
        // console.log(state.getCurrentContent().getPlainText());

        // Check if editor is empty
        // if (!state.getCurrentContent().hasText()) {
        //     console.log("empty");
        // }

        const textLength = content.blocks.reduce((acc, block) => acc + block.text.length + 1, 0) - 1;
        setCharCount(textLength);
    };

    const handlePastedNoteText = (text, _html, state) => {
        const content = state.getCurrentContent();
        const selection = state.getSelection();
        const contentWithoutSelection = Modifier.removeRange(content, selection, 'forward');
        const availableCharacters = Math.max(
            0,
            MAX_NOTE_LENGTH - contentWithoutSelection.getPlainText('').length
        );

        // Keep the editor's normal paste behavior, including rich formatting, when everything fits.
        if (text.length <= availableCharacters) return 'not-handled';

        const truncatedText = text.slice(0, availableCharacters);
        let nextContent = contentWithoutSelection;

        if (truncatedText) {
            const pastedFragment = ContentState.createFromText(truncatedText).getBlockMap();
            nextContent = Modifier.replaceWithFragment(content, selection, pastedFragment);
        }

        let nextState = EditorState.push(state, nextContent, 'insert-fragment');
        nextState = EditorState.forceSelection(nextState, nextContent.getSelectionAfter());

        // MUIRichTextEditor owns its Draft state. Updating defaultValue causes it to accept the
        // truncated state while onEditorChange keeps the form payload and counter synchronized.
        setDefaultRteValue(JSON.stringify(convertToRaw(nextContent)));
        onEditorChange(nextState);
        setPasteLimitAlert(true);
        return 'handled';
    };

    function isJSON(str) {
        try {
            JSON.parse(str);
            return true;
        } catch (e) {
            return false;
        }
    }

    const checkCompleteness = async () => {
        if (!editorState.getCurrentContent().hasText()) {
            setErrorAlert('Please write some note content before checking completeness.');
            return;
        }
        setGuidanceLoading(true);
        setGuidanceError('');
        setGuidanceResult(null);
        setGuidanceOpen(true);
        try {
            const response = await axios.post(`${APIURL}/comments/guidance`, {
                note_text: noteText,
            });
            setGuidanceResult(response.data);
        } catch (err) {
            console.error(err);
            setGuidanceError(err?.response?.data?.message || 'Failed to check completeness. Please try again.');
        } finally {
            setGuidanceLoading(false);
        }
    };

    const getStrategyChipProps = (status) => {
        if (status === 'Covered') return { icon: <CheckCircleOutlineIcon />, color: 'success' };
        if (status === 'Partially Covered') return { icon: <WarningAmberIcon />, color: 'warning' };
        return { icon: <CancelOutlinedIcon />, color: 'error' };
    };

    const getIndicatorChipProps = (status) => {
        if (status === 'Captured') return { icon: <CheckCircleOutlineIcon />, color: 'success' };
        return { icon: <CancelOutlinedIcon />, color: 'error' };
    };

    // Non-modal floating window: the note stays fully editable while it is open.
    const renderGuidanceWindow = () => {
        if (!guidanceOpen) return null;

        const score = guidanceResult?.completeness_score;
        const strategies = guidanceResult?.strategies || [];
        const indicators = Object.entries(guidanceResult?.person_centered_indicators || {});
        const coveredStrategiesCount = strategies.filter(s => s.status === 'Covered' || s.status === 'Partially Covered').length;
        const capturedIndicatorsCount = indicators.filter(([, ind]) => ind.status === 'Captured').length;
        const tips = guidanceResult?.guidance_tips?.length
            ? guidanceResult.guidance_tips
            : ['Note is complete and well-documented!'];

        return (
            <Portal>
                <Draggable
                    handle=".guidance-drag-handle"
                    cancel=".guidance-no-drag"
                    nodeRef={guidanceRef}
                    position={guidancePosition}
                    onStop={(_e, data) => {
                        const next = keepGuidanceOnScreen({ x: data.x, y: data.y });
                        setGuidancePosition(next);
                        try {
                            localStorage.setItem(GUIDANCE_POSITION_KEY, JSON.stringify(next));
                        } catch (err) {
                            console.log(err);
                        }
                    }}
                >
                    <Paper
                        ref={guidanceRef}
                        elevation={8}
                        sx={{
                            position: 'fixed',
                            left: 24,
                            bottom: `${TOURS_LAUNCHER_CLEARANCE}px`,
                            zIndex: GUIDANCE_WINDOW_ZINDEX,
                            width: 380,
                            maxWidth: 'calc(100vw - 48px)',
                            maxHeight: '70vh',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid #e0e7ef',
                        }}
                    >
                    <Box
                        className="guidance-drag-handle"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            backgroundColor: '#eef2ff',
                            borderBottom: '1px solid #e0e7ef',
                            cursor: 'move',
                            userSelect: 'none',
                        }}
                    >
                        <DragIndicatorIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                        <AutoFixHighIcon sx={{ fontSize: 20, color: '#5c6bc0' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', flex: 1 }}>
                            AI Guidance
                        </Typography>
                        {!guidanceLoading && !guidanceError && guidanceResult && (
                            <Chip
                                label={`${score}%`}
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    backgroundColor: scoreColor(score),
                                    color: '#fff',
                                    height: 22,
                                    fontSize: '0.75rem',
                                }}
                            />
                        )}
                        <IconButton
                            className="guidance-no-drag"
                            size="small"
                            aria-label="close guidance"
                            onClick={() => setGuidanceOpen(false)}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 1.5, overflowY: 'auto' }}>
                        {guidanceLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 2 }}>
                                <CircularProgress size={20} sx={{ color: '#5c6bc0' }} />
                                <Typography variant="body2" sx={{ color: '#546e7a', fontWeight: 500 }}>
                                    Evaluating case note completeness...
                                </Typography>
                            </Box>
                        )}

                        {!guidanceLoading && guidanceError && (
                            <Typography variant="body2" color="error">{guidanceError}</Typography>
                        )}

                        {!guidanceLoading && !guidanceError && guidanceResult && (
                            <>
                                {tips.map((tip, index) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                        <TipsAndUpdatesIcon sx={{ fontSize: 18, color: '#f59e0b', mt: 0.2 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', lineHeight: 1.45 }}>
                                            {tip}
                                        </Typography>
                                    </Box>
                                ))}

                                <Divider sx={{ my: 1.5 }} />

                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                                    Service Strategies ({coveredStrategiesCount}/{strategies.length})
                                </Typography>
                                <Box sx={{ mt: 0.75 }}>
                                    {strategies.map((s) => {
                                        const { icon, color } = getStrategyChipProps(s.status);
                                        return (
                                            <Box key={s.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1 }}>
                                                {React.cloneElement(icon, { fontSize: 'small', color })}
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                                        {s.name}
                                                    </Typography>
                                                    {s.evidence && (
                                                        <Typography variant="caption" sx={{ display: 'block', color: '#64748b', lineHeight: 1.35 }}>
                                                            {s.evidence}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>

                                <Divider sx={{ my: 1.5 }} />

                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                                    Person-Centered Details ({capturedIndicatorsCount}/{indicators.length})
                                </Typography>
                                <Box sx={{ mt: 0.75 }}>
                                    {indicators.map(([key, ind]) => {
                                        const { icon, color } = getIndicatorChipProps(ind.status);
                                        const label = key === 'behavioral_triggers'
                                            ? 'Behavioral Triggers'
                                            : key.charAt(0).toUpperCase() + key.slice(1);
                                        return (
                                            <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.75 }}>
                                                {React.cloneElement(icon, { fontSize: 'small', color })}
                                                <Typography variant="body2" sx={{ color: '#334155' }}>
                                                    <strong>{label}:</strong> {ind.value || 'Not specified'}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </>
                        )}
                    </Box>
                    </Paper>
                </Draggable>
            </Portal>
        );
    };

    const headingText = props.mode === 'edit' ? 'Edit Note' : !props.isVoiceNote ? 'New Note' : 'New Voice Note';
    const submitLabel = props.mode === 'edit' ? 'Update Note' : !props.isVoiceNote ? 'Create Note' : 'Create Voice Note';
    const handleSubmitClick = props.mode === 'edit' ? updateNote : !props.isVoiceNote ? createNote : createVoiceNote;
    const submitDisabled = props.isVoiceNote ? !recordedAudio : !editorState?.getCurrentContent().hasText();
    const twoColumn = props.twoColumn !== false;

    const detailsSection = (
        <>
                <Autocomplete
                    noOptionsText='Loading Customers...'
                    size="small"
                    disabled={props.mode === 'edit' || props.project_id != null || props.customer_id != null}
                    sx={{ mt: 2, mb: 1.5, width: '100%' }}
                    value={selectedCustomer}
                    onChange={async (e, value, reason) => {
                        if (reason == 'clear') {
                            setSelectedCustomer(null);
                        }
                        else if (value) {
                            setSelectedCustomer(value);
                        }
                    }}
                    options={customerList}
                    getOptionLabel={(option) => option.cus_name}
                    renderInput={(params) => (
                        <TextField {...params} label="Customer" variant="outlined" size="small" />
                    )}
                />
                <Autocomplete
                    noOptionsText='Loading Projects...'
                    size="small"
                    disabled={props.mode === 'edit' || props.project_id != null || selectedCustomer == null}
                    sx={{ mb: 1.5, width: '100%' }}
                    value={selectedProject}
                    onChange={(e, value, reason) => {
                        if (reason == 'clear') {
                            setSelectedProject(null);
                        }
                        else if (value) {
                            setSelectedProject(value);
                        }
                    }}
                    options={projectList}
                    getOptionLabel={(option) => option.pro_title}
                    renderInput={(params) => (
                        <TextField {...params} label="Project" variant="outlined" size="small" />
                    )}
                />
                <Autocomplete
                    noOptionsText='No Tasks Found'
                    size="small"
                    disabled={props.mode === 'edit' || props.task_id != null || selectedProject == null}
                    value={selectedTask}
                    disablePortal
                    sx={{
                        mb: 1.5,
                        width: '100%',
                        fontWeight: 'bold',
                        '& + .MuiAutocomplete-popper .MuiAutocomplete-option': {
                            fontWeight: 'bold',
                        }
                    }}
                    onChange={(e, value, reason) => {
                        if (reason == 'clear') {
                            setSelectedTask(null);
                        }
                        else if (value) {
                            setSelectedTask(value);
                            console.log();
                        }
                    }}
                    options={taskList}
                    getOptionLabel={(option) => `${option.tas_title}`} //TODO add PROPER square bracketed date before the task title like '[10-10-2024] My Task Title' 
                    renderOption={(props, option) => (
                        <li {...props} style={{ color: option.tas_title === "Add New Task (Timesheet)" ? '#1976D2' : "" }}>
                            {option.tas_title}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField {...params} label="Task" variant="outlined" size="small" />
                    )}
                />

                {
                    selectedTask !== undefined && selectedTask !== null && props.mode !== 'edit' && selectedTask.tas_id === -1 &&
                    <div>
                        <TextField
                            label="Task Title"
                            size="small"
                            value={taskTitle}
                            onChange={(e) => setTaskTitle(e.target.value)}
                            inputProps={{ maxLength: 50 }}
                            sx={{ mb: 1.5, width: '100%' }}
                        />

                        <Autocomplete
                            sx={{ mb: 1.5, width: '100%' }}
                            options={taskTypes}
                            value={selectedTaskType}
                            getOptionLabel={(option) => option}
                            onChange={(event, newValue) => setSelectedTaskType(newValue)}
                            renderInput={(params) => <TextField {...params} label="Task Type" size="small" />}
                        />

                        {useDurationMode ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', width: '100%', justifyContent: 'space-between' }}>
                                <LocalizationProvider class='DatePickerParent' dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Task Date"
                                        className={`mx-1 my-3`}
                                        value={moment(selectedDate).isValid() ? new Date(moment(selectedDate)) : null}
                                        onChange={(d) => { 
                                            const newDate = dayjs(d).format("YYYY-MM-DD");
                                            setSelectedDate(newDate);

                                            // Only update the date part, keep the time part
                                            setStartTime(prev => {
                                                if (!prev) return ""; // handle empty case
                                                const time = dayjs(prev).format("HH:mm:ss");
                                                return `${newDate}T${time}`;
                                            });
                                            setEndTime(prev => {
                                                if (!prev) return ""; // handle empty case
                                                const time = dayjs(prev).format("HH:mm:ss");
                                                return `${newDate}T${time}`;
                                            });
                                        }}
                                        slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
                                    />
                                </LocalizationProvider>

                                <FormControl size="small" sx={{ marginTop: "17px", flex: 1, minWidth: 120 }}>
                                    <InputLabel id="duration-label">Duration</InputLabel>
                                    <Select
                                        labelId="duration-label"
                                        value={duration}
                                        onChange={(e) => {
                                            setDuration(e.target.value);
                                            const [hours, minutes] = e.target.value.split(':').map(Number);
                                            setStartTime(moment(selectedDate).set({ hour: 9, minute: 0 }).format('YYYY-MM-DDTHH:mm:ss'));
                                            setEndTime(moment(selectedDate).set({ hour: 9 + hours, minute: minutes }).format('YYYY-MM-DDTHH:mm:ss'));
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 48 * 8,
                                                },
                                            },
                                        }}
                                    >
                                        {durations.map((duration) => (
                                            <MenuItem key={duration} value={duration}>{duration}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        ) : (
                            <div style={{ width: '100%' }}>
                                <TextField
                                    label="Start Time"
                                    size="small"
                                    type="datetime-local"
                                    value={moment(startTime).format('YYYY-MM-DDTHH:mm')}
                                    onChange={(e) => {
                                        // datetime-local gives 'YYYY-MM-DDTHH:mm'; SQL Server DATETIME needs seconds
                                        if (e.target.value) {
                                            setStartTime(`${e.target.value}:00`);
                                        }
                                    }}
                                    sx={{ mb: 2.5, width: '100%' }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                                <TextField
                                    label="End Time"
                                    size="small"
                                    type="datetime-local"
                                    value={moment(endTime).format('YYYY-MM-DDTHH:mm')}
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            setEndTime(`${e.target.value}:00`);
                                        }
                                    }}
                                    sx={{ mb: 1.5, width: '100%' }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </div>
                        )}
                    </div>
                }

                <TextField
                    value={noteTitle}
                    size="small"
                    onChange={(event) => setNoteTitle(event.target.value)}
                    sx={{ mb: 1.5, width: '100%' }}
                    inputProps={{ maxLength: 50 }}
                    disabled={props.mode === 'edit'}
                    label="Note Title"
                    id="nTitle"
                />
                <FormControl style={{ margin: '5px', marginBottom: '10px', width: '100%' }}>
                    <RadioGroup
                        row
                        sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
                        value={notePrivacy}
                        onChange={(e) => setNotePrivacy(e.target.value)}
                    >
                        <FormControlLabel
                            value="shared"
                            control={<Radio />}
                            label="Shared with All"
                            sx={{ mr: 2, '.MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                        />
                        <FormControlLabel
                            value="private"
                            control={<Radio />}
                            label="Private to Program"
                            disabled={!canSetPrivateNote}
                            sx={{ mr: 0, '.MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                        />
                    </RadioGroup>
                </FormControl>
        </>
    );

    const noteSection = (
        <>
                {/* {!props.isVoiceNote &&
                    <TextField
                        multiline
                        value={noteText}
                        onChange={(event) => {
                            setNoteText(event.target.value)
                        }}
                        style={{ margin: '5px', marginBottom: '16px', width: '100%' }}
                        inputProps={{ maxLength: 8000 }}
                        minRows={4}
                        label="Notes"
                        variant="outlined"
                        id="aNote"
                    />
                } */}
                {!props.isVoiceNote &&
                    <ThemeProvider theme={myTheme}>
                        <MUIRichTextEditor
                            draftEditorProps={{
                                spellCheck: true,
                                handlePastedText: handlePastedNoteText
                            }}
                            label="Click here to start typing..."
                            defaultValue={defaultRteValue}
                            onChange={(state) => {
                                onEditorChange(state);
                                const contentState = state.getCurrentContent();
                                const newText = contentState.getPlainText();
                                const isDirty = newText !== initialPlainText.current;
                                console.log(isDirty, newText, initialPlainText.current);
                                
                                props.onDirtyChange && props.onDirtyChange(isDirty);
                            }}
                            maxLength={MAX_NOTE_LENGTH}
                            controls={["title","bold","italic","underline","strikethrough","highlight","undo","redo","link","media","bulletList","numberList","quote","code","clear","save","voice-to-text"]}
                            customControls={[
                                {
                                    name: "voice-to-text",
                                    icon: engaged.current ? <StopIcon color={"error"} /> : <MicIcon color={"action"} />,
                                    type: "callback",
                                    onClick: (_editorState, name, _anchor) => {
                                        if (!listening) {
                                            engaged.current = true;
                                            SpeechRecognition.startListening({ continuous: true });
                                        } else {
                                            engaged.current = false;
                                            SpeechRecognition.stopListening();
                                        }
                                        changeTheEditorState(_editorState);
                                        // let currEditorState = EditorState.createWithContent(_editorState.getCurrentContent())
                                        // let selection = currEditorState.getSelection().merge({
                                        //     anchorKey: currEditorState.getCurrentContent().getFirstBlock().getKey(),
                                        //     anchorOffset: 0,  

                                        //     focusOffset: currEditorState.getCurrentContent().getLastBlock().getText().length, 
                                        //     focusKey: currEditorState.getCurrentContent().getLastBlock().getKey(),
                                        // })
                                        // let newEditorState = EditorState.forceSelection(currEditorState, selection);
                                        // console.log(newEditorState.getSelection().getStartKey(), newEditorState.getSelection().getEndKey());

                                        // const nextContentState = Modifier.replaceText(currEditorState.getCurrentContent(), selection, currEditorState.getCurrentContent().getPlainText() + "inserted this here");
                                        // console.log(nextContentState.getPlainText());
                                        // let nextEditorState = EditorState.createEmpty();
                                        // nextEditorState = EditorState.push(editorState, nextContentState, 'insert-characters');
                                        // setEditorState(nextEditorState);
                                        // setNoteText(JSON.stringify(convertToRaw(nextEditorState.getCurrentContent())));
                                        // onEditorChange(nextEditorState);
                                        // return nextEditorState;
                                    }
                                }
                            ]}
                        />
                        <div style={{
                            float: 'right',
                            fontSize: "12px",
                            color: charCount === MAX_NOTE_LENGTH ? "red" : "gray",
                        }}>
                            {charCount} / {MAX_NOTE_LENGTH}
                        </div>
                    </ThemeProvider>
                }

                {/* Check Completeness Button */}
                {!props.isVoiceNote && (
                    <Button
                        variant="outlined"
                        fullWidth
                        sx={{
                            padding: '8px',
                            mt: 1,
                            mb: 1,
                            borderColor: '#5c6bc0',
                            color: '#5c6bc0',
                            '&:hover': {
                                borderColor: '#3949ab',
                                backgroundColor: '#e8eaf6',
                            },
                        }}
                        onClick={checkCompleteness}
                        disabled={guidanceLoading || !editorState?.getCurrentContent().hasText()}
                        startIcon={<AutoFixHighIcon />}
                    >
                        {guidanceLoading ? 'Checking...' : 'Check Completeness'}
                    </Button>
                )}

                {props.isVoiceNote &&
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            {!isRecording && (
                                <Button onClick={onStartRecording}><i className="fas fa-microphone"></i></Button>
                            )}
                            {isRecording && (
                                <div>
                                    <Button style={{ marginRight: '10px' }} onClick={onStopRecording}><i className="fas fa-stop-circle"></i></Button>
                                    <span>Recording Time: {timer} seconds</span>
                                </div>
                            )}
                            {!isRecording && recordedAudio && !audioURL && <Button style={{ marginLeft: '10px' }} onClick={onPlayRecording}><i className="fas fa-play-circle"></i></Button>}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                            {audioURL && (
                                <audio controls autoPlay src={audioURL} />
                            )}
                        </div>
                    </div>
                }
        </>
    );

    return (
        <div className='noteForm-mainDiv'>
            {twoColumn ? (
                <Box sx={{ textAlign: 'left', px: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f3a5f', mb: 1 }}>
                        {headingText}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            alignItems: 'stretch',
                        }}
                    >
                        <SectionCard
                            icon={<ListAltOutlinedIcon sx={{ color: '#1976d2' }} />}
                            title="Note Details"
                            flex="40 1 0"
                        >
                            {detailsSection}
                        </SectionCard>

                        <SectionCard
                            icon={<NoteAltOutlinedIcon sx={{ color: '#1976d2' }} />}
                            title={props.isVoiceNote ? 'Voice Note' : 'Note'}
                            flex="60 1 0"
                        >
                            {noteSection}
                        </SectionCard>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => handleSubmitClick()}
                            disabled={submitDisabled}
                        >
                            {submitLabel}
                        </Button>
                    </Box>
                </Box>
            ) : (
                <>
                    <h4>{headingText}</h4>
                    <div>
                        {detailsSection}
                        {noteSection}
                        <Button
                            variant="contained"
                            fullWidth
                            sx={{ padding: '10px', mt: 1 }}
                            onClick={() => handleSubmitClick()}
                            disabled={submitDisabled}
                        >
                            {submitLabel}
                        </Button>
                    </div>
                </>
            )}
            {!props.isVoiceNote && renderGuidanceWindow()}
            <Snackbar open={successAlert != ''} autoHideDuration={2000} onClose={handleSuccessAlertClose}>
                <MuiAlert sx={{ width: '100%' }} severity={"success"} elevation={6} variant="filled" {...props}>
                    {successAlert}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={handleSuccessAlertClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </MuiAlert>
            </Snackbar>
            <Snackbar open={errorAlert != ''} autoHideDuration={2000} onClose={handleErrorAlertClose}>
                <MuiAlert sx={{ width: '100%' }} severity={"error"} elevation={6} variant="filled" {...props}>
                    {errorAlert}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={handleErrorAlertClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </MuiAlert>
            </Snackbar>
            <Snackbar
                open={pasteLimitAlert}
                autoHideDuration={5000}
                onClose={(_, reason) => reason !== 'clickaway' && setPasteLimitAlert(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert
                    severity="warning"
                    elevation={6}
                    variant="filled"
                    onClose={() => setPasteLimitAlert(false)}
                    sx={{ width: '100%' }}
                >
                    Your note reached the {MAX_NOTE_LENGTH.toLocaleString()}-character limit. We pasted everything that fit and left out the rest.
                </MuiAlert>
            </Snackbar>
        </div>
    );
}
